/**
 * The superadmin switch palette's data model (⌘⇧K): which rows show, in what order, and which seat a
 * team row switches into. Pure, so the ordering and recents rules are unit-tested without React.
 */

/** One switchable seat, as `superAdmin.switchTargets` returns it. */
export type SwitchTarget = {
  userId: string;
  teamId: string;
  teamName: string | null;
  name: string;
  email: string;
  profilePicture: string | null;
  isActive: boolean;
  lastSendAt: Date | string | null;
  isAdmin?: boolean;
  linkedin?: 'connected' | 'error' | 'none';
  isDfy?: boolean;
};

/** What a Recent row remembers — enough to render it and switch without a fresh search. */
export type RecentTarget = Pick<SwitchTarget, 'userId' | 'teamId' | 'teamName' | 'name' | 'email' | 'profilePicture'>;

export type PalettePage = { href: string; title: string; description: string };

export type PaletteItem =
  | { kind: 'back'; id: 'back' }
  | { kind: 'person'; id: string; target: SwitchTarget | RecentTarget; recent: boolean }
  | { kind: 'team'; id: string; teamName: string; switchTo: SwitchTarget; memberCount: number }
  | { kind: 'page'; id: string; page: PalettePage };

export type PaletteSection = {
  key: 'back' | 'recent' | 'people' | 'teams' | 'pages';
  title: string;
  items: PaletteItem[];
};

export const RECENT_LIMIT = 5;
const PEOPLE_LIMIT = 12;
const TEAM_LIMIT = 6;
const PAGE_LIMIT = 6;

const norm = (s: string | null | undefined) => (s ?? '').toLowerCase().trim();
const time = (d: Date | string | null | undefined) => (d == null ? 0 : new Date(d).getTime());

/**
 * The seat a team row switches into: an admin first, then whoever sent most recently. Deterministic so the
 * row can say who you'll become before you press Enter.
 */
export function pickTeamPrimary(members: SwitchTarget[]): SwitchTarget | undefined {
  return [...members].sort(
    (a, b) =>
      Number(b.isAdmin === true) - Number(a.isAdmin === true) ||
      Number(b.isActive) - Number(a.isActive) ||
      time(b.lastSendAt) - time(a.lastSendAt) ||
      a.name.localeCompare(b.name),
  )[0];
}

/** Most-recent-first, de-duplicated, capped. */
export function pushRecent(list: RecentTarget[], target: RecentTarget, limit = RECENT_LIMIT): RecentTarget[] {
  const entry: RecentTarget = {
    userId: target.userId,
    teamId: target.teamId,
    teamName: target.teamName,
    name: target.name,
    email: target.email,
    profilePicture: target.profilePicture,
  };
  return [entry, ...list.filter((r) => r.userId !== target.userId)].slice(0, limit);
}

const nullableString = (v: unknown) => v == null || typeof v === 'string';

function isRecentTarget(r: unknown): r is RecentTarget {
  return (
    typeof r === 'object' &&
    r != null &&
    'userId' in r &&
    typeof r.userId === 'string' &&
    'teamId' in r &&
    typeof r.teamId === 'string' &&
    'name' in r &&
    typeof r.name === 'string' &&
    'email' in r &&
    typeof r.email === 'string' &&
    (!('teamName' in r) || nullableString(r.teamName)) &&
    (!('profilePicture' in r) || nullableString(r.profilePicture))
  );
}

/** Parse stored recents defensively — anything malformed is dropped, never thrown. */
export function parseRecents(raw: string | null): RecentTarget[] {
  if (raw == null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentTarget).slice(0, RECENT_LIMIT);
  } catch {
    return [];
  }
}

const matchesQuery = (q: string, ...fields: (string | null | undefined)[]) =>
  q === '' || fields.some((f) => norm(f).includes(q));

/**
 * Builds the palette's sections for a query.
 * - **Back to myself** first while impersonating (also matches "back"/"myself"/"stop").
 * - **Recent** only with an empty query — once you type, the search is the answer.
 * - **People** from the server search, active first (the server already orders them).
 * - **Teams** only when typing: teams whose name matches, each switching into its primary seat.
 * - **Superadmin pages** whose title or description matches; with no query, the "Daily" pages.
 */
export function buildPaletteSections(p: {
  query: string;
  targets: SwitchTarget[];
  recents: RecentTarget[];
  pages: PalettePage[];
  dailyPages: PalettePage[];
  impersonating: boolean;
  currentUserId?: string | null;
}): PaletteSection[] {
  const q = norm(p.query);
  const sections: PaletteSection[] = [];

  if (
    p.impersonating &&
    (q === '' || ['back', 'myself', 'stop', 'me'].some((w) => w.startsWith(q) || q.startsWith(w)))
  ) {
    sections.push({ key: 'back', title: '', items: [{ kind: 'back', id: 'back' }] });
  }

  if (q === '' && p.recents.length > 0) {
    sections.push({
      key: 'recent',
      title: 'Recent',
      items: p.recents
        .filter((r) => r.userId !== p.currentUserId)
        .map((target) => ({ kind: 'person' as const, id: `recent:${target.userId}`, target, recent: true })),
    });
  }

  const people = p.targets
    .filter((t) => t.userId !== p.currentUserId)
    .slice(0, PEOPLE_LIMIT)
    .map((target) => ({ kind: 'person' as const, id: `person:${target.userId}`, target, recent: false }));
  if (people.length > 0) sections.push({ key: 'people', title: q === '' ? 'Active people' : 'People', items: people });

  if (q !== '') {
    const byTeam = new Map<string, SwitchTarget[]>();
    for (const t of p.targets) {
      if (t.teamName == null || !norm(t.teamName).includes(q)) continue;
      byTeam.set(t.teamId, [...(byTeam.get(t.teamId) ?? []), t]);
    }
    const teams = [...byTeam.values()]
      .map((members) => ({ members, primary: pickTeamPrimary(members) }))
      .filter((t): t is { members: SwitchTarget[]; primary: SwitchTarget } => t.primary != null)
      .slice(0, TEAM_LIMIT)
      .map(({ members, primary }) => ({
        kind: 'team' as const,
        id: `team:${primary.teamId}`,
        teamName: primary.teamName ?? primary.teamId,
        switchTo: primary,
        memberCount: members.length,
      }));
    if (teams.length > 0) sections.push({ key: 'teams', title: 'Teams', items: teams });
  }

  const pages = (q === '' ? p.dailyPages : p.pages.filter((pg) => matchesQuery(q, pg.title, pg.description)))
    .slice(0, PAGE_LIMIT)
    .map((page) => ({ kind: 'page' as const, id: `page:${page.href}`, page }));
  if (pages.length > 0) sections.push({ key: 'pages', title: 'Superadmin pages', items: pages });

  return sections.filter((s) => s.items.length > 0);
}

/** Every row in display order — the order ↑/↓ walks. */
export const flattenSections = (sections: PaletteSection[]): PaletteItem[] => sections.flatMap((s) => s.items);

/** Next/previous row id, wrapping at the ends; starts at the first row when nothing is active. */
export function moveActive(items: PaletteItem[], activeId: string | null, delta: 1 | -1): string | null {
  if (items.length === 0) return null;
  const i = items.findIndex((it) => it.id === activeId);
  if (i === -1) return items[delta === 1 ? 0 : items.length - 1]!.id;
  return items[(i + delta + items.length) % items.length]!.id;
}
